import type { ClickableItem, IsDropdownItem, VisibleItem } from 'layouts/main/sp-header/navigation/models';
import { NavigationItem } from 'layouts/main/sp-header/navigation/NavigationItem';
import { IntlKey } from 'locales';
import { FormattedMessage } from 'react-intl';
import { connect, useSelector } from 'react-redux';
import { getProductReferentialLoadingStatus } from 'state/selectors';
import {
  isAdvancedWorkspaceVisible,
  isBatchWorkspaceVisible,
  isDynamicWorkspaceVisible,
  isEssentialWorkspaceVisible,
  isSecondaryBatchVisible,
  isSmartQuoteWorkspaceVisible,
  isSPLabVisible,
} from 'state/selectors/common/router';
import type { AppState } from 'state/state.model';
import { getSplabUrl } from 'state/user';

export function SmartQuoteWorkspaceItem({
  isDropdownItem,
  onClick,
  isVisible,
}: IsDropdownItem & ClickableItem & VisibleItem) {
  return (
    <NavigationItem
      isVisible={isVisible}
      category="Workspace"
      targetRoute="/smart-quote"
      action="Smart quote"
      label="appHeader.navigation.workspaces.smartQuote"
      dataE2eSuffix="smart-quote-screen"
      defaultMessage="Smart Quote workspace"
      isDropdownItem={isDropdownItem}
      onClick={onClick}
      isNewItem
    />
  );
}

export const ConnectedSmartQuoteWorkspaceItem = connect((state: AppState) => ({
  isVisible: isSmartQuoteWorkspaceVisible(state),
}))(SmartQuoteWorkspaceItem);

export function EssentialWorkspaceItem({
  isDropdownItem,
  onClick,
  isVisible,
}: IsDropdownItem & ClickableItem & VisibleItem) {
  return (
    <NavigationItem
      isVisible={isVisible}
      category="Workspace"
      targetRoute="/essential"
      action="Essential workspace"
      label="appHeader.navigation.workspaces.essential"
      dataE2eSuffix="essential-screen"
      defaultMessage="Essential workspace"
      isDropdownItem={isDropdownItem}
      onClick={onClick}
      isNewItem
    />
  );
}

export const ConnectedEssentialWorkspaceItem = connect((state: AppState) => ({
  isVisible: isEssentialWorkspaceVisible(state),
}))(EssentialWorkspaceItem);

export function AdvancedWorkspaceItem({
  isDropdownItem,
  onClick,
  isVisible,
}: IsDropdownItem & ClickableItem & VisibleItem) {
  return (
    <NavigationItem
      isVisible={isVisible}
      category="Workspace"
      targetRoute="/static"
      action="New static screen"
      label="appHeader.navigation.workspaces.advanced"
      dataE2eSuffix="new-static-screen"
      defaultMessage="Quote workspace"
      isDropdownItem={isDropdownItem}
      onClick={onClick}
    />
  );
}

export const ConnectedAdvancedWorkspaceItem = connect((state: AppState) => ({
  isVisible: isAdvancedWorkspaceVisible(state),
}))(AdvancedWorkspaceItem);

export function DynamicWorkspaceItem({
  isVisible,
  isDropdownItem,
  onClick,
}: VisibleItem & IsDropdownItem & ClickableItem) {
  return (
    <NavigationItem
      isVisible={isVisible}
      category="Workspace"
      targetRoute="/dynamic"
      action="New dynamic screen"
      label="appHeader.navigation.workspaces.dynamic"
      dataE2eSuffix="new-dynamic-screen"
      defaultMessage="New dynamic screen"
      isDropdownItem={isDropdownItem}
      onClick={onClick}
    />
  );
}

export const ConnectedDynamicWorkspaceItem = connect((state: AppState) => ({
  isVisible: isDynamicWorkspaceVisible(state),
}))(DynamicWorkspaceItem);

export function BatchWorkspaceItem({
  isVisible,
  isDropdownItem,
  onClick,
}: VisibleItem & IsDropdownItem & ClickableItem) {
  return (
    <NavigationItem
      isVisible={isVisible}
      category="Workspace"
      targetRoute="/batch"
      action="New batch screen"
      label="appHeader.navigation.workspaces.batch"
      dataE2eSuffix="new-batch-pricer"
      defaultMessage="New batch screen"
      isDropdownItem={isDropdownItem}
      onClick={onClick}
      title="appHeader.navigation.workspaces.batch.ariaLabel"
    />
  );
}
export const ConnectedBatchWorkspaceItem = connect((state: AppState) => ({
  isVisible: isBatchWorkspaceVisible(state) && getProductReferentialLoadingStatus(state, 'newBatch') === 'loaded',
}))(BatchWorkspaceItem);

export function SPLabItem({ onClick, isVisible, isDropdownItem }: VisibleItem & ClickableItem & IsDropdownItem) {
  const splabUrl = useSelector((state: AppState) => getSplabUrl(state)) || '#';

  return (
    <NavigationItem
      isVisible={isVisible}
      href={splabUrl}
      target="_blank"
      dataE2eSuffix="web-pricer"
      onClick={onClick}
      isDropdownItem={isDropdownItem}
      shouldNavigate={false}
      category="Workspace"
      action="SG Markets bespoke"
    >
      <>
        <FormattedMessage id="appHeader.navigation.workspaces.spLab" />
        <i className="icon icon-sm ms-2">launch</i>
      </>
    </NavigationItem>
  );
}

export const ConnectedSPLabItem = connect((state: AppState) => ({
  isVisible: isSPLabVisible(state),
}))(SPLabItem);

export type SecondaryMarketQuoteWorkspaceItemProps = { label?: IntlKey } & IsDropdownItem & ClickableItem;

export function SecondaryMarketQuoteWorkspaceItem({
  isDropdownItem,
  onClick,
  label,
}: SecondaryMarketQuoteWorkspaceItemProps) {
  return (
    <NavigationItem
      isDropdownItem={isDropdownItem}
      targetRoute="/secondary-market-quotes-workspace"
      label={label ?? 'appHeader.navigation.workspaces.secondary.quote'}
      dataE2eSuffix="secondary-quotes-workspace"
      category="Workspace"
      action="Secondary market quotes workspace"
      onClick={onClick}
    />
  );
}

export function SecondaryMarketBatchWorkspaceItem({
  isVisible,
  isDropdownItem,
  onClick,
}: VisibleItem & ClickableItem & IsDropdownItem) {
  return (
    <NavigationItem
      isVisible={isVisible}
      isDropdownItem={isDropdownItem}
      targetRoute="/secondary-market-batch"
      label={'appHeader.navigation.workspaces.secondary.batch'}
      dataE2eSuffix="secondary-quotes-workspace"
      category="Workspace"
      action="Secondary market batch workspace"
      onClick={onClick}
      isNewItem
      title="appHeader.navigation.workspaces.secondary.batch.ariaLabel"
    />
  );
}

export const ConnectedSecondaryMarketBatchWorkspaceItem = connect((state: AppState) => ({
  isVisible: isSecondaryBatchVisible(state),
}))(SecondaryMarketBatchWorkspaceItem);
